import React from 'react';
import { motion } from 'framer-motion';

const productsData = [
  {
    id: 1,
    title: "IT Service Management Accelerator (ITSM+)",
    description: "Streamline your IT operations with a pre-configured ITSM solution designed for rapid deployment.",
    image: "/assets/products/item1.png",
    imageAlt: "ITSM Abstract 3D Art",
    imageLeft: true,
    features: [
      "Incident, Problem & Change Management",
      "Service Catalog & Request Automation",
      "SLA & Performance Tracking",
      "Pre-built dashboards and reports"
    ],
    benefits: [
      "Faster implementation",
      "Reduced operational costs",
      "Improved service delivery"
    ]
  },
  {
    id: 2,
    title: "HR Service Delivery Suite (HRSD Pro)",
    description: "Enhance employee experience with a centralized HR service platform.",
    image: "/assets/products/item2.png",
    imageAlt: "HRSD Abstract Tech Waves",
    imageLeft: false,
    features: [
      "Employee self-service portal",
      "Case and query management",
      "Automated onboarding workflows",
      "Knowledge base integration"
    ],
    benefits: [
      "Increased HR efficiency",
      "Better employee engagement",
      "Faster resolution times"
    ]
  },
  {
    id: 3,
    title: "Customer Service Management Solution (CSM Smart)",
    description: "Deliver exceptional customer support with intelligent workflows.",
    image: "/assets/products/item3.png",
    imageAlt: "ITSM Abstract 3D Art",
    imageLeft: true,
    features: [
      "Incident, Problem & Change Management",
      "Service Catalog & Request Automation",
      "SLA & Performance Tracking",
      "Pre-built dashboards and reports"
    ],
    benefits: [
      "Faster implementation",
      "Reduced operational costs",
      "Improved service delivery"
    ]
  },
  {
    id: 4,
    title: "Workflow Automation Engine",
    description: "Automate repetitive tasks and business processes across departments.",
    image: "/assets/products/item4.png",
    imageAlt: "HRSD Abstract Tech Waves",
    imageLeft: false,
    features: [
      "Employee self-service portal",
      "Case and query management",
      "Automated onboarding workflows",
      "Knowledge base integration"
    ],
    benefits: [
      "Increased HR efficiency",
      "Better employee engagement",
      "Faster resolution times"
    ]
  },
  {
    id: 5,
    title: "ServiceNow Integration Hub (Custom Connect)",
    description: "Seamlessly connect ServiceNow with your enterprise ecosystem.",
    image: "/assets/products/item5.png",
    imageAlt: "ITSM Abstract 3D Art",
    imageLeft: true,
    features: [
      "Incident, Problem & Change Management",
      "Service Catalog & Request Automation",
      "SLA & Performance Tracking",
      "Pre-built dashboards and reports"
    ],
    benefits: [
      "Faster implementation",
      "Reduced operational costs",
      "Improved service delivery"
    ]
  },
  {
    id: 6,
    title: "Analytics & Reporting Dashboard (Insight Pro)",
    description: "Gain actionable insights with advanced reporting tools.",
    image: "/assets/products/item6.png",
    imageAlt: "HRSD Abstract Tech Waves",
    imageLeft: false,
    features: [
      "Employee self-service portal",
      "Case and query management",
      "Automated onboarding workflows",
      "Knowledge base integration"
    ],
    benefits: [
      "Increased HR efficiency",
      "Better employee engagement",
      "Faster resolution times"
    ]
  },
];

const ChevronListItem = ({ text }) => (
  <li className="relative pl-5 mb-3 text-[14px] md:text-[16px] font-graphik text-[#49577A] font-[500]">
    <span className="absolute left-0 top-0 text-orange-500 font-bold text-[16px]">&gt;</span>
    {text}
  </li>
);

export default function MobikodeProducts() {
  // Animation Variants
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  return (
    <section className="bg-[#FFFFFF] py-10 md:py-20 font-sora">
      <div className="max-w-[1400px] mx-auto px-[20px] lg:px-[80px]">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-[30px] md:mb-[50px]"
        >
          <h2 className="text-[20px] md:text-[30px] font-[600] text-[#01386A] tracking-tight">
            Explore Our Mobikode Products
          </h2>
        </motion.div>

        {/* Products List */}
        <div className="space-y-12 md:space-y-16">
          {productsData.map((product) => (
            <motion.div
              key={product.id}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="flex flex-col md:flex-row bg-white border border-[#BAC9D7] border-b-[4px] border-b-[#01386A] overflow-hidden transition-shadow duration-300 gap-8 md:gap-12 p-[20px] md:p-[60px]"
            >

              <div className={`w-full md:w-full max-w-[529px] flex items-center justify-center ${product.imageLeft ? 'md:order-1' : 'md:order-2'}`}>
                <div className="w-full h-full overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.imageAlt}
                    className="w-full h-[250px] md:h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              <div className={`w-full md:w-full max-w-[580px] flex flex-col justify-center ${product.imageLeft ? 'md:order-2' : 'md:order-1'}`}>
                <h3 className="text-[16px] md:text-[20px] font-[600] font-sora text-[#01386A] mb-4 leading-tight">
                  {product.title}
                </h3>
                <p className="text-[#49577A] font-graphik font-[500] mb-8 text-[14px] md:text-[16px] leading-relaxed">
                  {product.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
                  {/* Key Features */}
                  <div className="flex-1">
                    <h4 className="font-[600] text-[#01386A] font-sora mb-4 text-[16px] md:text-[20px]">
                      Key Features:
                    </h4>
                    <ul className="flex flex-col gap-1">
                      {product.features.map((feature, idx) => (
                        <ChevronListItem key={`feature-${idx}`} text={feature} />
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div className="flex-1">
                    <h4 className="font-[600] text-[#01386A] font-sora mb-4 text-[16px] md:text-[20px]">
                      Benefits:
                    </h4>
                    <ul className="flex flex-col gap-1">
                      {product.benefits.map((benefit, idx) => (
                        <ChevronListItem key={`benefit-${idx}`} text={benefit} />
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}